import React, { useState, useLayoutEffect, useRef } from "react";
import { Box, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button, CssBaseline, ThemeProvider } from "@mui/material";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import LockClockIcon from '@mui/icons-material/LockClock';
import { Routes, Route, Navigate, Outlet, useLocation, useOutlet } from "react-router-dom";

// Theme and Contexts
import { ColorModeContext, useMode } from "./theme";
import { AppProvider } from "./contexts/AppContext";
import { AuthProvider, BypassAuthProvider, useAuth } from "./contexts/AuthContext";
import { CustomKeycloakProvider } from "./contexts/CustomKeycloakAuth";

// Components
import Topbar from "./scenes/global/Topbar";
import Sidebar from "./scenes/global/Sidebar";
import Footer from "./scenes/global/Footer";
import Dashboard from "./scenes/dashboard";
import Dataset from "./scenes/datasets";
import RegisterDataset from "./scenes/datasets/registerDataset";
import Models from "./scenes/models";
import RegisterModel from "./scenes/models/RegisterModel";
import ClientApp from "./scenes/clientApp";
import Execute from "./scenes/execute";
import Configs from "./scenes/configs";
import Settings from "./scenes/settings";
import UsersRoles from "./scenes/usersRoles/Guard";
import AcceptInvite from "./scenes/auth/AcceptInvite";
import Sites from "./scenes/sites";
import CodeEditor from "./scenes/clientApp/codeEditor";
import Homepage from "./scenes/homepage";
import AuthLoading from "./scenes/auth/AuthLoading";
import PresentationLayer from './scenes/auth/DHDPPresentationNavigation'
import Documentation from "./scenes/documentation";
import "./i18n";

const pageVariants = {
  initial: (shouldAnimate) => (shouldAnimate ? {
    x: "100%",
    opacity: 0,
  } : {
    x: 0,
    opacity: 1,
  }),
  animate: (shouldAnimate) => (shouldAnimate ? {
    x: 0,
    opacity: 1,
    transition: {
      x: { type: "spring", stiffness: 300, damping: 30, restDelta: 0.5 },
      opacity: { duration: 0.2 }
    }
  } : {
    x: 0,
    opacity: 1,
    transition: { duration: 0 }
  }),
  exit: (shouldAnimate) => (shouldAnimate ? {
    x: "-20%",
    opacity: 0,
    transition: { duration: 0.3 }
  } : {
    x: 0,
    opacity: 1,
    transition: { duration: 0 }
  })
};

const AuthenticatedLayout = () => {
  const location = useLocation();
  const element = useOutlet();
  // Honor the OS "reduce motion" setting — users who request it get instant
  // page swaps instead of the spring slide transition.
  const prefersReducedMotion = useReducedMotion();
  const shouldAnimate = location.state?.animate && !prefersReducedMotion;

  return (
    <Box sx={{ display: "flex", height: "100vh", overflow: "hidden" }}>
      <Sidebar />
      <Box sx={{ flexGrow: 1, display: "flex", flexDirection: "column", minWidth: 0 }}>
        <Topbar />
        <Box sx={{ flexGrow: 1, position: 'relative', overflow: "hidden", backgroundColor: "var(--fx-canvas)" }}>
          <AnimatePresence mode="wait" custom={shouldAnimate} initial={false}>
            <motion.div
              key={location.pathname}
              custom={shouldAnimate}
              variants={pageVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              style={{
                height: "100%",
                width: "100%",
                // Always render the vertical scroll track (scroll, not auto) and
                // reserve its gutter. Without this, switching between a short tab
                // and a tall one toggles the scrollbar on/off, which reflows the
                // container and nudges the whole page up/down by a few pixels.
                overflowY: "scroll",
                overflowX: "auto",
                scrollbarGutter: "stable",
                backgroundColor: "var(--fx-canvas)"
              }}
            >
              {element}
            </motion.div>
          </AnimatePresence>
        </Box>
        <Footer />
      </Box>
    </Box>
  );
};

const ProtectedRoute = () => {
  const { isAuthenticated } = useAuth();
  const location = useLocation();
  // Preserve the originally-requested path. On a hard reload / deep link there
  // can be a render where `loading` has cleared but `isAuthenticated` is still
  // false (auth settling); previously we redirected to "/" with `replace`,
  // which then sent the user to /dashboard and silently dropped the deep link.
  // Carrying `from` lets RootRoute send them back to where they asked to go.
  return isAuthenticated
    ? <Outlet />
    : <Navigate to="/" replace state={{ from: location.pathname + location.search }} />;
};

// Root ("/") handler. Unauthenticated users see the marketing Homepage.
// Authenticated users are forwarded to their requested deep link (if one was
// preserved by ProtectedRoute) or to /dashboard by default.
const RootRoute = () => {
  const { isAuthenticated } = useAuth();
  const location = useLocation();
  if (!isAuthenticated) return <Homepage />;
  const from = location.state?.from;
  const target = from && from !== "/" ? from : "/dashboard";
  return <Navigate to={target} replace />;
};

const AppContent = () => {
  const { loading, sessionExpired } = useAuth();
  
  // if (!loading) return <PresentationLayer />;
  if (loading) return <AuthLoading />;

  return (
    <AppProvider>
      <Routes>
        <Route path="/" element={<RootRoute />} />
        <Route path="/documentation/*" element={<Documentation />} />
        <Route path="/accept-invite" element={<AcceptInvite />} />
        <Route element={<ProtectedRoute />}>
          <Route element={<AuthenticatedLayout />}>
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/datasets" element={<Dataset />} />
            <Route path="/datasets/registerDataset" element={<RegisterDataset />} />
            <Route path="/models" element={<Models />} />
            <Route path="/models/registerModel" element={<RegisterModel />} />
            <Route path="/clientApp" element={<ClientApp />} />
            <Route path="/clientApp/codeEditor" element={<CodeEditor />} />
            <Route path="/execute" element={<Execute />} />
            <Route path="/configs" element={<Configs />} />
            <Route path="/sites" element={<Sites />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="/users-roles" element={<UsersRoles />} />
          </Route>
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      
      <Dialog open={sessionExpired} disableEscapeKeyDown>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, width: '200px' }}>
          <LockClockIcon color="error" /> Session Expired
        </DialogTitle>
        <DialogContent sx={{ width: '200px' }}><DialogContentText sx={{ textAlign: 'center' }}>Please log in again.</DialogContentText></DialogContent>
        <DialogActions sx={{ justifyContent: 'center' }}>
          <Button onClick={() => window.location.href = window.location.origin} color="primary" variant="contained">Log In Again</Button>
        </DialogActions>
      </Dialog>
    </AppProvider>
  );
};

function App() {
  const [theme, colorMode] = useMode();
  const bypassAuth = import.meta.env.DEV && import.meta.env.VITE_BYPASS_AUTH === 'true';

  return (
    <ColorModeContext.Provider value={colorMode}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {bypassAuth ? (
          <BypassAuthProvider><AppContent /></BypassAuthProvider>
        ) : (
          <CustomKeycloakProvider><AuthProvider><AppContent /></AuthProvider></CustomKeycloakProvider>
        )}
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
}

export default App;